import { http, notify } from 'utils'
import { checkEmail } from 'utils/errorHelpers'
import { pick, findIndex, isObject, concat } from 'lodash'
import Vue from 'vue'

import { SET_ADMINS, SET_ADMINS_COUNTS, SET_ADMINS_PAGINATION, ADD_ADMINS, RESET_ADMINS, REMOVE_ADMIN } from 'vx/mutations'

// Admin state.
const state = {
  admin: null,
  admins: [],
  adminCounts: {
    all: 0,
    active: 0,
    inactive: 0
  },
  pagination: {}
}

// Admin state getters.
const getters = {
  /**
   * Determine if we have a single talent.
   *
   * @return bool
   */
  haveAdmin: (state) => {
    return state.booking !== null &&
      state.admin !== null
  },

  /**
   * Determine if we have no more talent to load.
   *
   * @return bool
   */
  haveNoMoreAdmins: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// Talent actions
const actions = {
  getAdminCounts ({ commit }, params = {}) {
    return http.get('admin-counts', { params })
      .then((response) => {
        commit(SET_ADMINS_COUNTS, response.data)

        return response
      })
  },

  getAdmins ({ commit }, params = {}) {
    commit(RESET_ADMINS)

    return http.get('admin', { params })
      .then((response) => {
        commit(SET_ADMINS, response.data.data)
        commit(SET_ADMINS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Delete a user.
   *
   * @return Promise
   */
  deleteAdmin ({ commit }, { id }) {
    return http.delete(`/admin/${id}`)
      .then((response) => {
        commit(REMOVE_ADMIN, response.data)
      })
  },

  /**
   * Get next set of paginated talents.
   *
   * @return Promise
   */
  getNextAdmins ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreAdmins) {
      return Promise.reject(new Error('There are no more talents to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`admin`, { params })
      .then((response) => {
        commit(ADD_ADMINS, response.data.data)
        commit(SET_ADMINS_PAGINATION, response.data.meta)

        return response
      })
  },

  storeAdmin (store, payload) {
    return http.post(`admin`, payload)
      .catch(error => {
        if (checkEmail(error)) {
          notify.error('That email has already been taken.')
        } else {
          notify.error('Something went wrong. Please check your input and try again.')
        }
      })
  },

  resetAdmins ({ commit }) {
    commit(RESET_ADMINS)
  }
}

// Talent state mutations.
const mutations = {

  [SET_ADMINS] (state, admins) {
    state.admins = admins
  },

  [SET_ADMINS_COUNTS] (state, adminCounts) {
    state.adminCounts = adminCounts
  },

  [SET_ADMINS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_ADMINS] (state, admins) {
    state.admins = concat(state.admins, admins)
  },

  [RESET_ADMINS] (state, admins) {
    state.admins = []
    state.pagination = {}
  },

  [REMOVE_ADMIN] (state, admin) {
    let existingIndex

    if (isObject(admin)) {
      existingIndex = findIndex(state.admins, existing => existing.id === admin.id)
    } else {
      existingIndex = findIndex(state.admins, existing => existing.id === admin)
    }

    if (existingIndex !== -1) {
      Vue.set(state.admins, existingIndex, admin)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
